import {UI_COLORS} from '@/utils/ui-colors';

const ALLOWED_POSITION_TABS = ['steps', 'project', 'costs', 'actions'];

export function getPositionManageTabStorageKey(routeId) {
    return `position-manage-active-tab:${String(routeId || '')}`;
}

export function isAllowedPositionManageTab(tab) {
    return ALLOWED_POSITION_TABS.includes(String(tab || ''));
}

export function persistPositionManageTab(routeId, tab) {
    if (!isAllowedPositionManageTab(tab)) {
        return;
    }

    try {
        window.localStorage.setItem(getPositionManageTabStorageKey(routeId), String(tab));
    } catch (error) {
        // Ignore storage errors (private mode/quota).
    }
}

export function restorePositionManageTab(routeId) {
    try {
        const savedTab = window.localStorage.getItem(getPositionManageTabStorageKey(routeId));
        return isAllowedPositionManageTab(savedTab) ? savedTab : null;
    } catch (error) {
        // Ignore storage access errors.
        return null;
    }
}

export function normalizePositionProjectIds(source) {
    const projectIds = Array.isArray(source?.project_ids)
        ? source.project_ids
        : (source?.project_ids && Array.isArray(source.project_ids.data) ? source.project_ids.data : []);

    if (projectIds.length > 0) {
        return projectIds
            .map((id) => Number(id))
            .filter((id) => id > 0);
    }

    const fallbackProjectId = Number(source?.project_id || 0);
    return fallbackProjectId > 0 ? [fallbackProjectId] : [];
}

export function getPositionProjectImageUrl(project) {
    return project && project.image_url ? project.image_url : '/images/default/icon.png';
}

export function getPositionProjectTooltip(project) {
    const title = String(project?.title || '').trim();
    return title !== '' ? `Projekt: ${title}` : 'Projekt';
}

export function normalizeHoursForPayload(value) {
    if (value === null || value === '' || typeof value === 'undefined') {
        return null;
    }

    const normalized = Number(value);
    if (Number.isNaN(normalized) || normalized < 0) {
        return null;
    }

    return Math.round(normalized * 100) / 100;
}

export function normalizeRateForPayload(value) {
    if (value === null || value === '' || typeof value === 'undefined') {
        return null;
    }

    const normalized = Number(String(value).replace(',', '.'));
    if (Number.isNaN(normalized) || normalized < 0) {
        return null;
    }

    return Math.round(normalized * 100) / 100;
}

export function normalizeQuantificationFactorForPayload(value) {
    const normalized = Number(value ?? 0);
    if (!Number.isFinite(normalized) || normalized < 0) {
        return 0;
    }

    return Math.max(0, Math.round(normalized));
}

export function normalizeQuantificationValueForPayload(value) {
    if (value === null || value === '' || typeof value === 'undefined') {
        return 0.25;
    }

    let normalized = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(normalized) || normalized < 0) {
        return 0.25;
    }

    if (normalized > 1 && normalized <= 100) {
        normalized = normalized / 100;
    }

    return Math.round(normalized * 10000) / 10000;
}

export function normalizeAssignedCompany(company) {
    if (!company || Number(company.id || 0) < 1) {
        return null;
    }

    return {
        ...company,
        id: Number(company.id),
    };
}

const DEFAULT_AVATAR_URL = 'https://www.gravatar.com/avatar/?d=mp';

export function getUniqueUsersById(users) {
    const list = Array.isArray(users) ? users : [];
    const seen = new Set();

    return list.filter((user) => {
        const id = Number(user && user.id);
        if (!id || seen.has(id)) {
            return false;
        }

        seen.add(id);
        return true;
    });
}

export function getPositionUserAvatarUrl(user) {
    if (!user) {
        return DEFAULT_AVATAR_URL;
    }

    if (user.avatar && user.avatar !== 'gravatar') {
        return user.avatar;
    }

    if (user.avatar_url && user.avatar_url !== 'gravatar') {
        return user.avatar_url;
    }

    if (user.gravatar && typeof user.gravatar === 'string' && user.gravatar.trim() !== '') {
        return user.gravatar;
    }

    if (user.image && typeof user.image === 'string' && user.image.trim() !== '') {
        return user.image;
    }

    if (user.picture && typeof user.picture === 'string' && user.picture.trim() !== '') {
        return user.picture;
    }

    return DEFAULT_AVATAR_URL;
}

export function getPositionApprovalAvatarUrl(user, customerCompanyImage = '') {
    const directAvatarUrl = getPositionUserAvatarUrl(user);
    if (directAvatarUrl && directAvatarUrl !== DEFAULT_AVATAR_URL) {
        return directAvatarUrl;
    }

    const companyImage = String(customerCompanyImage || '').trim();
    if (companyImage !== '') {
        return companyImage;
    }

    return directAvatarUrl;
}

export function getPositionEmployeeAvatarUrl(employee, employeePools = []) {
    const employeeId = Number(employee && employee.id);
    const employeeOptions = getUniqueUsersById(employeePools.flat());
    const matchedEmployee = employeeOptions.find((reader) => Number(reader.id) === employeeId);

    return getPositionUserAvatarUrl(matchedEmployee || employee);
}

export function getEmployeeAssignmentSubtitle(employee) {
    const providerCompanyName = String(employee && employee.provider_company_name ? employee.provider_company_name : '').trim();
    return providerCompanyName !== '' ? providerCompanyName : 'Kein Dienstleister';
}

export function getAssignedEmployeesByIds(selectedIds, employeeOptions = []) {
    const normalizedIds = Array.isArray(selectedIds)
        ? [...new Set(selectedIds.map((id) => Number(id)).filter((id) => id > 0))]
        : [];

    if (normalizedIds.length < 1) {
        return [];
    }

    const options = getUniqueUsersById(employeeOptions);
    const optionsById = new Map(options.map((employee) => [Number(employee.id), employee]));

    return getUniqueUsersById(
        normalizedIds
            .map((id) => optionsById.get(id))
            .filter((employee) => !!employee)
    );
}

export function applyAvatarImageFallback(event, user) {
    if (!event || !event.target) {
        return;
    }

    const fallback = user && typeof user.gravatar === 'string' && user.gravatar.trim() !== ''
        ? user.gravatar
        : DEFAULT_AVATAR_URL;

    if (fallback && event.target.src !== fallback) {
        event.target.src = fallback;
        return;
    }

    event.target.src = DEFAULT_AVATAR_URL;
}

export function applyApprovalAvatarImageFallback(event, user, customerCompanyImage = '') {
    if (!event || !event.target) {
        return;
    }

    const companyImage = String(customerCompanyImage || '').trim();
    if (companyImage !== '' && event.target.src !== companyImage) {
        event.target.src = companyImage;
        return;
    }

    applyAvatarImageFallback(event, user);
}

export function formatPositionStepDate(value) {
    return moment(value).format('DD.MM.YYYY');
}

export function isPositionStepOverdue(processStep) {
    if (!processStep || !processStep.end_date || processStep.is_completed) {
        return false;
    }

    return moment(processStep.end_date).endOf('day').isBefore(moment());
}

export function getApprovalStatusLabel(status) {
    return (status || 'pending') === 'approved' ? 'freigegeben' : 'ausstehend';
}

export function getApprovalStatusText(item) {
    const label = getApprovalStatusLabel(item && item.status);
    const dateSource = label === 'freigegeben' ? item && item.approved_at : item && item.created_at;
    if (!dateSource) {
        return label;
    }

    return `${label} ${moment(dateSource).format('DD.MM.YYYY')}`;
}

export function getApprovalCheckinOrDraftText(item) {
    if (!item || !item.is_checked_in || !item.checked_in_at) {
        return 'Entwurf';
    }

    return moment(item.checked_in_at).format('DD.MM.YYYY');
}

export function getApprovalApprovedAtText(item) {
    const status = item && item.status ? item.status : 'pending';
    if (!item) {
        return 'ausstehend';
    }

    if (status === 'cancelled') {
        if (item.cancelled_at) {
            return moment(item.cancelled_at).format('DD.MM.YYYY');
        }

        return 'storniert';
    }

    if (status !== 'approved' || !item.approved_at) {
        return 'ausstehend';
    }

    return moment(item.approved_at).format('DD.MM.YYYY');
}

export function getApprovalApprovedAtDateTimeText(item) {
    const status = item && item.status ? item.status : 'pending';
    if (!item) {
        return 'ausstehend';
    }

    if (status === 'cancelled') {
        if (item.cancelled_at) {
            return `Storniert am ${moment(item.cancelled_at).format('DD.MM.YYYY [um] HH:mm')}`;
        }

        return 'Storniert';
    }

    if (status !== 'approved' || !item.approved_at) {
        return 'ausstehend';
    }

    return moment(item.approved_at).format('DD.MM.YYYY [um] HH:mm');
}

export function getApprovalEffortHoursText(item, canSeeEffortAmounts) {
    if (!item || !canSeeEffortAmounts) {
        return '';
    }

    const parseNumber = (value) => {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const customerHours = parseNumber(item.customer_hours);
    const internalHours = parseNumber(item.internal_hours);
    const hours = customerHours !== null ? customerHours : internalHours;

    if (hours === null) {
        return '';
    }

    return `${hours.toFixed(2).replace('.', ',')} Std.`;
}

export function getApprovalStatusBorderColor(status) {
    if ((status || 'pending') === 'cancelled') {
        return '#ef4444';
    }

    return (status || 'pending') === 'approved' ? '#22c55e' : UI_COLORS.border.subtle;
}

export function withOpacity(color, opacity) {
    const fallback = `rgba(156, 163, 175, ${opacity})`;
    if (!color || typeof color !== 'string') {
        return fallback;
    }

    const value = color.trim();
    if (value.startsWith('#')) {
        let hex = value.slice(1);
        if (hex.length === 3) {
            hex = hex.split('').map((c) => c + c).join('');
        }
        if (hex.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(hex)) {
            return fallback;
        }
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    const rgbMatch = value.match(/^rgb\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/i);
    if (rgbMatch) {
        return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
    }

    const rgbaMatch = value.match(/^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*[\d.]+\s*\)$/i);
    if (rgbaMatch) {
        return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
    }

    return fallback;
}

export function getProcessStepTimelineDotStyle(color) {
    const baseColor = color || UI_COLORS.text.subtle;

    return {
        backgroundColor: baseColor,
        outline: `6px solid ${withOpacity(baseColor, 0.1)}`,
        outlineOffset: '0px',
    };
}

export function hasStepBody(processStep) {
    return !!processStep && String(processStep.body || '').trim() !== '';
}

export function formatStepBodyUpdatedAt(value) {
    return moment(value).format('DD.MM.YYYY HH:mm');
}

export function formatBodyUpdateMeta(updatedAt, user) {
    if (!updatedAt || !user) {
        return '';
    }

    const userName = (user.name || '').trim();
    const dateText = moment(updatedAt).format('DD.MM.YYYY');
    const timeText = moment(updatedAt).format('HH:mm');

    return `${userName} am ${dateText} um ${timeText}`.trim();
}
